import React, { useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// Voice dictation button — records with MediaRecorder and transcribes via the
// backend's Emergent-managed OpenAI whisper-1 endpoint (/ai/transcribe).
// Works in every modern browser (incl. Firefox/Safari) and handles Indian
// accents + medical vocabulary far better than the old Web Speech API.
// Appends the transcript to `value` via `onChange`. Auto-stops at 90 s.
export default function MicButton({ value, onChange, lang = "en", testid, ariaLabel = "Dictate" }) {
  const [state, setState] = useState("idle"); // idle | recording | busy
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const supported = typeof window !== "undefined" && !!(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined";

  const stopTracks = (rec) => { try { rec?.stream?.getTracks().forEach((t) => t.stop()); } catch (e) { console.debug("[silent]", e); } };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        clearTimeout(timerRef.current);
        stopTracks(rec);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 1500) { setState("idle"); return; } // accidental tap
        setState("busy");
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "").includes("mp4") ? "mp4" : "webm";
          fd.append("file", blob, `dictation.${ext}`);
          fd.append("language", (lang || "en").slice(0, 2));
          const r = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (r.data.text || "").trim();
          if (text) {
            const prev = valueRef.current || "";
            const sep = prev && !prev.endsWith(" ") ? " " : "";
            onChange(prev + sep + text);
          } else {
            toast.info("No speech detected — try again closer to the mic");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed — try again");
        }
        setState("idle");
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
      timerRef.current = setTimeout(() => { try { rec.stop(); } catch (e) { console.debug("[silent]", e); } }, 90000);
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone permission denied" : "Could not access the microphone");
      setState("idle");
    }
  };

  const toggle = () => {
    if (state === "busy") return;
    if (state === "recording") { try { recRef.current?.stop(); } catch (e) { console.debug("[silent]", e); } return; }
    start();
  };

  if (!supported) {
    return (
      <button type="button" disabled className="p-1.5 rounded text-slate-300 cursor-not-allowed" title="Voice dictation is not supported in this browser" data-testid={testid}>
        <MicOff size={14} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={ariaLabel}
      className={`p-1.5 rounded transition-colors ${
        state === "recording" ? "bg-red-100 text-red-600 animate-pulse"
        : state === "busy" ? "text-pink-600"
        : "text-slate-500 hover:bg-slate-100 hover:text-pink-600"
      }`}
      title={state === "recording" ? "Stop & transcribe" : state === "busy" ? "Transcribing…" : "Dictate (AI transcription)"}
      data-testid={testid}
    >
      {state === "busy" ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
    </button>
  );
}
